import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InfoCircleIcon,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@customink/pigment-react';
import {
  usePricing,
  useProject,
} from '@customink/project-service-react-components';
import type { ECMCertificateDto } from '@customink/project-service-react-components/src/services/accounts/types';
import { TAX_EXEMPTION_STATUSES } from '@customink/project-service-types';
import Collapse from '@mui/material/Collapse';
import { useAuth } from 'editor-app/src/contexts/auth/AuthContext';
import { useCallback, useMemo, useState } from 'react';

import { ApplyTaxExemptionDialogECM } from './ApplyTaxExemptionDialogECM';
import { EmbeddedTaxExemptionDialog } from './EmbeddedPortal/EmbeddedTaxExemptionDialog';
import { useFeatureFlagEnabled } from '../../hooks/FeatureFlags/useFeatureFlagEnabled';

import { LoginLink } from '@/components/LoginLink';
import {
  isPending,
  isValid,
  usStatesAndTerritories,
} from '@/components/TaxExemption/util';
import { useGetCertificates } from '@/hooks/Accounts/TaxExemptionECM/useGetCertificates';
import { useInternalPreview } from '@/hooks/internalPreview';
import { useUpdateProjectConfigTaxExemption } from '@/hooks/Project';
import {
  PgmntColorInteractiveIconSubtle,
  PgmntColorInteractiveTextWeak,
  PgmntColorNeutralBackgroundSubtle,
  PgmntRadiusCard,
} from '@/utils/designTokens';
import { fireEvent, pageViewTag } from '@/utils/metrics';

export const TAX_EXEMPTION_VERIFICATION_METHODS = {
  VERIFY_NOW: 'verifyNow',
  CONTINUE_WITH_PURCHASE: 'continueWithPurchase',
} as const;

const exemptionContainerStyles = {
  backgroundColor: PgmntColorNeutralBackgroundSubtle,
  padding: '0.375rem 0.5rem',
  marginTop: '0.5rem',
  borderRadius: PgmntRadiusCard,
  width: 'fit-content',
};

export function TaxExemptionLineItem() {
  const { data: project } = useProject();
  const { data: pricingData } = usePricing();
  const {
    data: taxExemptionAllCertificates,
    refetch: refetchTaxExemptionCertificates,
  } = useGetCertificates();

  const { user } = useAuth();
  const [updateTaxExempt] = useUpdateProjectConfigTaxExemption();
  const isInternalPreview = useInternalPreview();

  // Don't render tax exemption features in internal preview mode
  if (isInternalPreview) {
    return null;
  }

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<string>(
    TAX_EXEMPTION_VERIFICATION_METHODS.CONTINUE_WITH_PURCHASE,
  );
  const isETEEnabled = useFeatureFlagEnabled('embedded_tax_exemptions');

  const handleRefetchTaxExemptionCertificates = useCallback(() => {
    const refetchAttempts = 4;
    const refetchInterval = 10000;

    const refetchTaxExemptions = async () => {
      const { data: newTaxExemptionsAllCertificates } =
        await refetchTaxExemptionCertificates();
      const isAccountTaxExempt = newTaxExemptionsAllCertificates
        ?.filter(isValid)
        ?.map((cert: ECMCertificateDto) => cert.exposureZoneRegion)
        .includes(state);
      if (isAccountTaxExempt) {
        await updateTaxExempt(TAX_EXEMPTION_STATUSES.EXEMPT);
      }
      return { isAccountTaxExempt };
    };

    // Refetch tax exemptions and stop in case the order is tax exempt
    refetchTaxExemptions().then(async ({ isAccountTaxExempt }) => {
      if (isAccountTaxExempt) {
        return;
      }

      let refetchItemCounter = 1;
      const interval = setInterval(async () => {
        if (refetchItemCounter < refetchAttempts) {
          const { isAccountTaxExempt } = await refetchTaxExemptions();
          if (isAccountTaxExempt) {
            clearInterval(interval);
          }
          refetchItemCounter++;
        } else {
          clearInterval(interval);
        }
      }, refetchInterval);
    });
  }, []);

  const onCloseDialog = (hasVerified = false) => {
    setShowDialog(false);
    if (hasVerified) {
      handleRefetchTaxExemptionCertificates();
    }
  };

  const projectTaxExemptionStatus = useMemo(
    () => project?.configuration?.taxExemption,
    [project?.configuration?.taxExemption],
  );
  const isUserLoggedIn = user?.isLoggedIn;
  const state = Object.values(pricingData.grouped)[0]?.state ?? 'Unknown State';
  const isInvalidState =
    state === 'Unknown State' || usStatesAndTerritories[state] === undefined;

  const isAccountTaxExempt = useMemo(
    () =>
      isUserLoggedIn &&
      taxExemptionAllCertificates
        ?.filter(isValid)
        ?.map((cert: ECMCertificateDto) => cert.exposureZoneRegion)
        .includes(state),
    [isUserLoggedIn, state, taxExemptionAllCertificates],
  );
  const havePendingAccountTaxExempt = useMemo(
    () =>
      isUserLoggedIn &&
      taxExemptionAllCertificates
        ?.filter(isPending)
        ?.map((cert: ECMCertificateDto) => cert.exposureZoneRegion)
        .includes(state),
    [isUserLoggedIn, state, taxExemptionAllCertificates],
  );

  const handleApplyTaxExemptionClick = () => {
    fireEvent('Order Summary', 'Link Click', 'Apply Tax Exemption');
    if (!isUserLoggedIn) {
      setShowLoginPrompt((prev) => !prev);
    } else {
      setShowDialog(true);
      pageViewTag('checkout/summary/TaxExempt');
    }
  };

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateTaxExempt(
        event.target.checked
          ? TAX_EXEMPTION_STATUSES.EXEMPT
          : TAX_EXEMPTION_STATUSES.NON_EXEMPT,
      );
    },
    [],
  );

  return isAccountTaxExempt ? (
    <Stack alignItems="center" direction="row" sx={exemptionContainerStyles}>
      <FormControlLabel
        control={
          <Checkbox
            checked={
              projectTaxExemptionStatus === TAX_EXEMPTION_STATUSES.EXEMPT
            }
            data-testid="tax-exempt-checkbox"
            onChange={handleCheckboxChange}
          />
        }
        label="This purchase is tax exempt"
        style={{ margin: '-4px 0px' }}
      />
      <Tooltip title="Tax exemption applies only to qualifying purchases in states in which you have tax exempt status.">
        <IconButton
          color="inherit"
          size="small"
          sx={{
            margin: '-8px 0 -8px 2px',
            color: PgmntColorInteractiveIconSubtle,
          }}
        >
          <InfoCircleIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : (
    <Box>
      {projectTaxExemptionStatus === TAX_EXEMPTION_STATUSES.CLAIMED ? (
        isETEEnabled ? (
          <Typography
            component="div"
            sx={exemptionContainerStyles}
            variant="paragraphSmall"
          >
            Tax Exemption Pending
            <Tooltip
              arrow
              placement="bottom"
              title="We will notify you via email with your approval status or any next steps. You can complete your purchase now and receive a tax refund once approved."
            >
              <IconButton
                color="inherit"
                size="small"
                sx={{
                  margin: '-8px 0 -8px 2px',
                  color: PgmntColorInteractiveIconSubtle,
                }}
              >
                <InfoCircleIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Typography>
        ) : (
          <Typography
            component="div"
            sx={exemptionContainerStyles}
            variant="paragraphSmall"
          >
            * Your sales tax will be refunded to your chosen payment method
            after your tax exemption status is approved.
          </Typography>
        )
      ) : (
        <Link
          color={PgmntColorInteractiveTextWeak}
          data-testid="apply-tax-exempt-link"
          onClick={handleApplyTaxExemptionClick}
          sx={{
            textDecoration: 'underline',
            display: 'flex',
            marginTop: '-12.5px',
          }}
          variant="uiTextTiny"
        >
          {' '}
          Tax exempt status?
        </Link>
      )}
      {projectTaxExemptionStatus === TAX_EXEMPTION_STATUSES.CLAIMED &&
        isETEEnabled && (
          <Typography component="div" variant="paragraphSmall">
            <Link
              data-testid="change-verification-tax-exempt-method"
              onClick={handleApplyTaxExemptionClick}
            >
              Change verification method
            </Link>
          </Typography>
        )}

      <Collapse in={showLoginPrompt}>
        <Typography
          component="div"
          sx={exemptionContainerStyles}
          variant="paragraphSmall"
        >
          Please <LoginLink /> to apply tax exemption to this purchase.
        </Typography>
      </Collapse>
      {isETEEnabled && showDialog && (
        <EmbeddedTaxExemptionDialog
          havePendingAccountTaxExempt={havePendingAccountTaxExempt}
          isInvalidState={isInvalidState}
          onClose={onCloseDialog}
          onRefetchTaxExemptionCertificates={
            handleRefetchTaxExemptionCertificates
          }
          onVerificationMethodChange={setVerificationMethod}
          open={showDialog}
          projectTaxExemptionStatus={projectTaxExemptionStatus}
          state={usStatesAndTerritories[state]}
          verificationMethod={verificationMethod}
        />
      )}
      {!isETEEnabled &&
        projectTaxExemptionStatus !== TAX_EXEMPTION_STATUSES.NON_EXEMPT && (
          <ApplyTaxExemptionDialogECM
            onClose={onCloseDialog}
            open={showDialog}
            state={state}
          />
        )}
    </Box>
  );
}
